// (2) Exam page wrapper: loads session + paper, sets up Socket.io, the 1s timer,
// the 30s effort heartbeat, and ENFORCED auto-submit.
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';
import type { Attempt, Paper, Question, QuestionResult } from '@/types';
import { attemptedCount, useExamStore } from '@/store/examStore';
import { useExamEvents } from '@/hooks/useExamEvents';
import { useEffortSync } from '@/hooks/useEffortSync';
import { useExamIntegrity } from '@/hooks/useExamIntegrity';
import { ExamHeader } from '@/components/exam/ExamHeader';
import { QuestionPicker } from '@/components/exam/QuestionPicker';
import { AnswerPanel } from '@/components/exam/AnswerPanel';
import { ExamSubmitFlow } from '@/components/exam/ExamSubmitFlow';

interface SessionResponse {
  session: { id: string; paperId: string; status: string };
  attempts: Attempt[];
  results: QuestionResult[];
}
interface PaperResponse {
  paper: Paper;
  questions: Question[];
}

export function ExamPage() {
  const { spId = null } = useParams();
  const navigate = useNavigate();

  const initSession = useExamStore((s) => s.initSession);
  const hydrateServerState = useExamStore((s) => s.hydrateServerState);
  const tick = useExamStore((s) => s.tick);
  const setActiveQuestion = useExamStore((s) => s.setActiveQuestion);

  const paper = useExamStore((s) => s.paper);
  const questions = useExamStore((s) => s.questions);
  const results = useExamStore((s) => s.results);
  const activeQuestionId = useExamStore((s) => s.activeQuestionId);
  const elapsedSeconds = useExamStore((s) => s.elapsedSeconds);
  const attempted = useExamStore(attemptedCount);

  // Live updates + heartbeats.
  useExamEvents(spId);
  useEffortSync(spId);
  // Server-authoritative question timing + soft proctoring signals.
  useExamIntegrity(spId, activeQuestionId);

  // Load session, then the paper it points at.
  const sessionQuery = useQuery({
    queryKey: ['session', spId],
    enabled: Boolean(spId),
    queryFn: () => apiGet<SessionResponse>(`/student-papers/${spId}`),
  });

  const paperId = sessionQuery.data?.session.paperId ?? null;
  const paperQuery = useQuery({
    queryKey: ['paper', paperId],
    enabled: Boolean(paperId),
    queryFn: () => apiGet<PaperResponse>(`/papers/${paperId}`),
  });

  // Initialize and hydrate in one ordered step. Hydrating as soon as the session
  // arrived could race the later paper initialization, which reset the freshly
  // loaded attempts and made a resumed exam appear untouched.
  useEffect(() => {
    if (spId && paperQuery.data && sessionQuery.data) {
      initSession({ spId, paper: paperQuery.data.paper, questions: paperQuery.data.questions });
      hydrateServerState(sessionQuery.data.attempts, sessionQuery.data.results);
    }
  }, [spId, paperQuery.data, sessionQuery.data, initSession, hydrateServerState]);

  // 1-second elapsed timer.
  useEffect(() => {
    if (!paper) return;
    const id = window.setInterval(() => tick(), 1000);
    return () => window.clearInterval(id);
  }, [paper, tick]);

  const autoSubmit = useCallback(async () => {
    if (!spId) return;
    try {
      await apiPost(`/student-papers/${spId}/submit`);
    } finally {
      navigate(`/reports/${spId}`);
    }
  }, [spId, navigate]);

  const resultsArray = useMemo<QuestionResult[]>(() => Object.values(results), [results]);
  const submittedCount = resultsArray.length;

  if (sessionQuery.isLoading || paperQuery.isLoading) {
    return <CenterMessage>Loading your exam…</CenterMessage>;
  }
  if (sessionQuery.isError || paperQuery.isError || !paper) {
    return <CenterMessage>Couldn't load this exam session. Please reopen the link.</CenterMessage>;
  }

  return (
    <div className="flex min-h-full flex-col">
      <ExamHeader
        total={questions.length}
        attempted={attempted}
        submitted={submittedCount}
        elapsedSeconds={elapsedSeconds}
        totalSeconds={paper.totalSeconds}
        timerMode={paper.timerMode}
        onTimeUp={autoSubmit}
      />

      <main className="flex-1">
        {activeQuestionId ? (
          <AnswerPanel onClose={() => setActiveQuestion('')} />
        ) : (
          <QuestionPicker
            questions={questions}
            results={resultsArray}
            onPick={(id) => setActiveQuestion(id)}
          />
        )}
      </main>

      {!activeQuestionId && <ExamSubmitFlow />}
    </div>
  );
}

function CenterMessage({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center px-6 text-center text-slate-500">
      {children}
    </div>
  );
}
